import { FaEraser, FaSearch } from "react-icons/fa";
import Button from "../../../../components/button/button";
import { Card } from "../../../../components/card/card";
import InputForm from "../../../../components/input/input-form";
import { Template } from "../../../../components/template/template";
import { AgGridReact } from "ag-grid-react";
import AgGridUtils from "../../../../utils/aggrid-utils";
import { useQuery } from "@tanstack/react-query";
import ContactsService from "../../../../services/web/contacts-service";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";

const norm = (s) => (s ?? "").toString().normalize("NFD").toLowerCase().trim();

const buildInitialFilters = (searchParams) => {
    const filters = [];

    const names = searchParams.get("names");
    if (names) {
        const uniq = [...new Set(names.split(",").map((s) => s.trim()).filter(Boolean))];
        if (uniq.length) filters.push({ field: "name", type: "in", value: uniq });
    };

    const cities = searchParams.get("cities");
    if (cities) {
        const uniq = [...new Set(cities.split(",").map((s) => s.trim()).filter(Boolean))];
        if (uniq.length) filters.push({ field: "city", type: "in", value: uniq });
    };

    return filters;
};

const makeFilter = (field, raw) => {
    const v = (raw ?? "").trim();
    if (!v) return null;

    if (v.includes(",")) {
        return { field, type: "in", value: v.split(",").map((x) => x.trim()).filter(Boolean) }
    };

    return { field, type: "like", value: v };
};

const applyFilters = (rows, filters) => {
    if (!filters?.length) return rows;

    return rows.filter((row) => filters.every((f) => {
        const cell = norm(row?.[f.field]);
        if (f.type === "like") return cell.includes(norm(f.value));
        if (f.type === "in") {
            const arr = Array.isArray(f.value) ? f.value : [];
            const set = new Set(arr.map(norm));
            return set.has(cell);
        };
        return true;
    }))
};

export default function SharedInstitutions() {
    const [corporation, setCorporation] = useState("");
    const [city, setCity] = useState("");
    const [searchParams] = useSearchParams();
    const [appliedFilters, setAppliedFilters] = useState(() => buildInitialFilters(searchParams));
    const ibge = useMemo(() => searchParams.get("ibge"), [searchParams]);

    const { data: institutions = [], isLoading} = useQuery({
        queryKey: ["institutions", ibge],
        queryFn: () => ContactsService.institutions({
            page: 1, size: 500, ibge, sort: [{field: "name", dir: "asc"}]
        }),
        select: (res) => res?.data?.data ?? [],
    });

    const filteredInstitutions = useMemo(() => applyFilters(institutions, appliedFilters),[institutions, appliedFilters]);

    const columnDefs = useMemo(() => ([
        { headerName: "Corporação", field: "name", flex: 2 },
        {
            headerName: "Município",
            field: "city",
            cellRenderer: (p) => {
                const rowIbge = p.data?.ibge;
                if (!rowIbge) return p.value ?? "";
                return (
                    <a
                        style={{ textDecoration: "underline", cursor: "pointer", color: "inherit" }}
                        onClick={() => window.open(ContactsService.agentsIbge(rowIbge))}
                    >
                        {p.value}
                    </a>
                );
            },
        },
        { headerName: "Telefone", field: "phone", flex: 1, sortable: true, comparator: AgGridUtils.nullLastComparator, },
        { headerName: "E-mail", field: "mail", flex: 2, sortable: true, comparator: AgGridUtils.nullLastComparator},
    ]), []);

    const handleSearch = () => {
        const filters = [
            makeFilter("name", corporation),
            makeFilter("city", city),
        ].filter(Boolean);

        setAppliedFilters(filters);
    };

    const handleClear = () => {
        setCorporation("");
        setCity("");
        setAppliedFilters([]);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
        if (e.key === "Escape") handleClear();
    };

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Corporações</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2 flex flex-col">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4 items-end">
                            <InputForm 
                                id="corporation"
                                label="Corporação"
                                classNameWrapper="flex-1"
                                value={corporation}
                                onChange={(e) => setCorporation(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <InputForm 
                                id="city"
                                label="Município"
                                classNameWrapper="flex-1"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <div className="flex gap-2">
                                <Button
                                    target="primary-outline"
                                    title="Limpar"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex">
                        <div className="h-[calc(100vh-153px)] w-full">
                            <AgGridReact 
                                rowData={filteredInstitutions}
                                columnDefs={columnDefs}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme()}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}